import React, { useEffect, useMemo, useState } from 'react';
import { Box, useTheme, Icons, IconButton } from '@ublend-npm/aulaui-core';
import { Post } from '@core/types/state';
import useBreakpoints from '@app/hooks/useBreakpoints';
import pushRoute from '@app/utils/routing';
import { getCommonRoomPath } from '@app/actions/navigation';
import { getCurrentSpaceId } from '@core/selectors/space';
import { accessingCurrentSpaceAsStaff } from '@core/selectors/classroom';
import { COMMON_ROOM } from '@core/constants/postFeedType';
import type { AulaDispatch, AulaState } from '@core/types/state';
import { useDispatch, useSelector } from 'react-redux';
import { replace } from 'react-router-redux';
import NoResultsCard from '@app/components/presentational/common/NoResults/NoResultsCard';
import { createStyles } from './CommonRoomResponsive.styles';
import CommonRoomPostCard from './CommonRoomPostCard.container';
import CommonRoomTabs, { CommonRoomTab } from './CommonRoomTabs';
import ComposePost from './ComposePost.container';
import SidebarPinnedPost from './Sidebar/PinnedPosts/PinnedPost';
import StudentAction from './StudentAction/StudentAction';
import ScheduledPostsSummaryContainer from './ScheduledPostsSummary';
import type { CreatePostAuthor } from '../Shared/ComposePost/ComposePost';

interface CommonRoomFeedResponsiveProps {
  posts?: Post[];
  mobilePosts?: Post[];
  pinnedPosts?: Post[];
  recentPinnedPosts?: Post[];
  readOnly?: boolean;
  pinnedCount?: number;
  author?: CreatePostAuthor;
  onTabChange?: (tab: CommonRoomTab) => void;
}

type CommonRoomLocation = {
  pathname: string;
  query?: { [key: string]: string };
};

type CommonRoomState = AulaState & {
  routing: { locationBeforeTransitions: CommonRoomLocation };
};

const getTabStorageKey = (spaceId: string) =>
  `responsiveCommonRoomTab:${spaceId}`;

const getStoredTab = (spaceId: string): CommonRoomTab => {
  if (!spaceId) return 'feed';
  try {
    return window.sessionStorage.getItem(getTabStorageKey(spaceId)) === 'pinned'
      ? 'pinned'
      : 'feed';
  } catch (_error) {
    return 'feed';
  }
};

const storeTab = (spaceId: string, tab: CommonRoomTab) => {
  if (!spaceId) return;
  try {
    if (tab === 'pinned') {
      window.sessionStorage.setItem(getTabStorageKey(spaceId), tab);
    } else {
      window.sessionStorage.removeItem(getTabStorageKey(spaceId));
    }
  } catch (_error) {
    // Route state still preserves the tab when storage is unavailable.
  }
};

const CommonRoomFeedResponsive = ({
  posts = [],
  mobilePosts = [],
  pinnedPosts = [],
  recentPinnedPosts = [],
  readOnly = false,
  pinnedCount,
  onTabChange,
}: CommonRoomFeedResponsiveProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const { md } = useBreakpoints();
  const dispatch = useDispatch<AulaDispatch>();
  const spaceId = useSelector(getCurrentSpaceId);
  const location = useSelector(
    (state: CommonRoomState) => state.routing?.locationBeforeTransitions
  );
  const routeTab = location?.query?.commonRoomTab;
  const [activeTab, setActiveTab] = useState<CommonRoomTab>(() =>
    routeTab === 'pinned' ? 'pinned' : getStoredTab(spaceId)
  );
  const isStaffAccess = useSelector(accessingCurrentSpaceAsStaff);

  useEffect(() => {
    if (md) {
      setActiveTab('feed');
    } else {
      setActiveTab(routeTab === 'pinned' ? 'pinned' : getStoredTab(spaceId));
    }
  }, [md, routeTab, spaceId]);

  const handleTabChange = (tab: CommonRoomTab) => {
    setActiveTab(tab);
    storeTab(spaceId, tab);
    if (location?.pathname) {
      const query = { ...(location.query || {}) };
      if (tab === 'pinned') query.commonRoomTab = 'pinned';
      else delete query.commonRoomTab;
      dispatch(replace({ ...location, query }));
    }
    if (onTabChange) {
      onTabChange(tab);
    }
  };

  const isPinnedSelected = activeTab === 'pinned';
  const recentPinnedPostIds = useMemo(
    () => new Set(recentPinnedPosts.map((post) => post.objectId)),
    [recentPinnedPosts]
  );
  const orderedPinnedPosts = useMemo(
    () => [
      ...recentPinnedPosts,
      ...pinnedPosts.filter((post) => !recentPinnedPostIds.has(post.objectId)),
    ],
    [pinnedPosts, recentPinnedPostIds, recentPinnedPosts]
  );
  let displayedPosts = posts;
  if (!md) {
    displayedPosts = isPinnedSelected ? orderedPinnedPosts : mobilePosts;
  }
  const showFeedOnlyContent = md || !isPinnedSelected;
  const commonRoomPath = getCommonRoomPath(spaceId);
  // handle compose post mobile
  const handleComposePostMobile = () => {
    pushRoute(`${commonRoomPath}/new`);
  };

  return (
    <Box sx={styles.feedLayout} data-testid="responsive-feed-layout">
      {md && !isStaffAccess && (
        <Box
          component="section"
          aria-label="Post composer"
          sx={styles.composerRegion}
        >
          <ComposePost feedType={COMMON_ROOM} />
        </Box>
      )}
      {!md && (
        <Box
          component="section"
          aria-label="Feed controls"
          sx={styles.feedControlsRegion}
        >
          <CommonRoomTabs
            activeTab={activeTab}
            pinnedCount={pinnedCount}
            onTabChange={handleTabChange}
          />
          {!isStaffAccess && (
            <IconButton
              aria-label="Compose post"
              onClick={handleComposePostMobile}
              sx={styles.createPostButton}
            >
              <Icons.DriveFileRenameOutline />
            </IconButton>
          )}
        </Box>
      )}
      <Box
        component="section"
        aria-label={
          isPinnedSelected ? 'Pinned Common Room posts' : 'Common Room feed'
        }
        sx={styles.postsRegion}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
          }}
        >
          {showFeedOnlyContent && !isStaffAccess && (
            <StudentAction theme={theme} />
          )}
        </Box>
        <Box sx={styles.postsList}>
          {showFeedOnlyContent && <ScheduledPostsSummaryContainer />}
          {!md && isPinnedSelected && displayedPosts.length === 0 && (
            <NoResultsCard
              title="No pinned post"
              description="Pin an important post to keep it easily accessible here."
              sx={styles.pinnedEmptyState}
              titleSx={styles.pinnedEmptyTitle}
            />
          )}
          {displayedPosts.map((post) => {
            const isRecentlyPinnedPost =
              !md && isPinnedSelected && recentPinnedPostIds.has(post.objectId);

            if (!md && isPinnedSelected) {
              return (
                <SidebarPinnedPost
                  key={post.objectId}
                  post={post}
                  variant="mobile"
                  isRecentlyPinnedPost={isRecentlyPinnedPost}
                />
              );
            }

            return (
              <CommonRoomPostCard
                key={post.objectId}
                post={post}
                readOnly={readOnly}
                isRecentlyPinnedPost={isRecentlyPinnedPost}
                showPinnedPostAlert={
                  showFeedOnlyContent || isRecentlyPinnedPost
                }
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default CommonRoomFeedResponsive;
